// Phase 33 §2g · 20c — Live Chat console.
//
// Three panes: the conversations, the one you are in, and who you are talking
// to. The list is ordered by WAIT, not recency — the conversation nobody
// answered goes to the top — which is the whole argument of the screen and a
// deliberate reversal of #9681's recency order (see chatConsole.ts).
//
// Two things in the artboard are NOT here, on purpose:
//   · the "pattern spotted" block — it needs retrieval over past chats, and a
//     stubbed one would be a machine claiming to have noticed something.
//   · Attach and Insert article — sendMessage has no attachment path and there
//     is no article URL convention to insert. Canned replies stay: `content`
//     is a field designed to be pasted.
// Everything the old screen could do survives: accept, send, transfer,
// end-with-ticket, the AI handoff summary, and the linked ticket.

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { ArrowDown, ChevronLeft, MessageCircle, RefreshCw, X } from 'lucide-react';
import { chatService } from '@/services/chat.service';
import { cannedResponsesService, type CannedResponse } from '@services/canned-responses.service';
import { teamMembersService, type TeamMember } from '@services/team-members.service';
import { ticketService } from '@services/ticket.service';
import type { Ticket } from '@centraoneplus/shared-types';
import { useAuthStore } from '@stores/authStore';
import { useChatWebSocket } from '@/hooks/useChatWebSocket';
import { MarkdownRenderer } from '@/components/MarkdownRenderer';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { duration } from '@shared/format/duration';
import type { ChatSession, ChatMessage, ChatStats } from '@/types/chat';
import { ChatSessionStatus, MessageType, SenderType } from '@/types/chat';
import {
  byWait,
  chatTimeline,
  consoleSubtitle,
  dayLabel,
  inCohort,
  sentimentLabel,
  startsDay,
  isBotHandled,
  lastLine,
  ownerLabel,
  visitorName,
  waitClock,
  waitSeconds,
  waitTone,
  type ChatCohort,
  type WaitTone,
} from './chatConsole';

/** The floating shell: cards sit ON the canvas, they do not butt against it. */
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-lg border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY =
  'flex h-[34px] flex-none items-center rounded-lg bg-[var(--co-brass-ink)] px-[14px] ' +
  'text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/** Header-only variant: the header's controls take the Board's 10px radius. */
const HEADER_CONTROL = CONTROL.replace('rounded-lg', 'rounded-[10px]');

/** The same roles the rest of the app treats as admin; the server applies the same rule. */
const ADMIN_ROLES = ['admin', 'owner'];

// Once an agent accepts a chat it is theirs alone until it is transferred: an
// agent sees the shared queue and their own chats; an admin sees every chat.
const AGENT_COHORTS: { key: ChatCohort; label: string }[] = [
  { key: 'waiting', label: 'Waiting' },
  { key: 'active', label: 'My chats' },
  { key: 'closed', label: 'My closed today' },
];
const ADMIN_COHORTS: { key: ChatCohort; label: string }[] = [
  { key: 'waiting', label: 'Waiting' },
  { key: 'active', label: 'All active' },
  { key: 'bot', label: 'With the bot' },
  { key: 'closed', label: 'Closed today' },
];

/** The server's reason for a refused request (409 taken, 403 someone else's). */
function apiMessage(err: any, fallback: string): string {
  return String(err?.response?.data?.message || err?.message || fallback);
}

function memberName(m: TeamMember): string {
  return m.displayName || [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email;
}

function startOfToday(now: number): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

const WAIT_COLOR: Record<WaitTone, string> = {
  danger: 'var(--co-terracotta)',
  warn: 'var(--co-terracotta)',
  normal: 'var(--co-ink-2)',
  none: 'var(--co-ink-3)',
};

/** Strip cid: URL references that cause ERR_UNKNOWN_URL_SCHEME errors. */
function sanitizeContent(content: string): string {
  return content
    .replace(/!\[([^\]]*)\]\(cid:[^)]+\)/g, '[$1]')
    .replace(/<img[^>]+src=["']cid:[^"']+["'][^>]*\/?>/gi, '');
}

/** The send shortcut as this keyboard writes it. The shortcut itself is ⌘/Ctrl+Enter on both. */
const SEND_KEYS =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘ Enter' : 'Ctrl + Enter';

/** Within this many pixels of the bottom, the agent is reading the latest. */
const NEAR_BOTTOM_PX = 80;

/** How long live updates may be down before saying so (a page load connects within it). */
const OFFLINE_GRACE_MS = 4000;

/** What the open chat's state is, in words — never colour alone. */
function sessionState(
  s: ChatSession,
  holds: boolean,
  agentNames: Record<string, string>,
): { label: string; color: string; wash: string; line: string } {
  switch (s.status) {
    case ChatSessionStatus.WAITING:
      return { label: 'In queue', color: 'var(--co-terracotta)', wash: 'var(--co-terracotta-wash)', line: 'var(--co-terracotta-line)' };
    case ChatSessionStatus.BOT:
      return { label: 'With the assistant', color: 'var(--co-machine-ink)', wash: 'var(--co-machine-wash)', line: 'var(--co-machine-line)' };
    case ChatSessionStatus.ACTIVE:
      return {
        label: holds ? 'With you' : `With ${agentNames[s.agentId ?? ''] || 'another agent'}`,
        color: 'var(--co-green)',
        wash: 'var(--co-green-wash)',
        line: 'var(--co-green-line)',
      };
    default:
      return { label: 'Ended', color: 'var(--co-ink-3)', wash: 'var(--co-selected)', line: 'var(--co-line-strong)' };
  }
}

function initial(name: string): string {
  return (name.trim()[0] || '?').toUpperCase();
}

function clock(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function LiveChatPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const seesAll = ADMIN_ROLES.includes((user?.role ?? '').toLowerCase());
  const cohorts = seesAll ? ADMIN_COHORTS : AGENT_COHORTS;
  const [cohort, setCohort] = useState<ChatCohort>('waiting');
  const [agentFilter, setAgentFilter] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<ChatSession | null>(null);
  const selectedIdRef = useRef<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [stats, setStats] = useState<ChatStats | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showEndChatModal, setShowEndChatModal] = useState(false);
  const [isEndingChat, setIsEndingChat] = useState(false);
  const [openTickets, setOpenTickets] = useState<Ticket[] | null>(null);
  const [canned, setCanned] = useState<CannedResponse[]>([]);
  const [showCanned, setShowCanned] = useState(false);
  const [agents, setAgents] = useState<TeamMember[]>([]);
  const [showTransfer, setShowTransfer] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // A ref as well as state: two quick ⌘↵ presses land before a re-render.
  const sendingRef = useRef(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const scrolledSessionRef = useRef<string | null>(null);
  const [unseen, setUnseen] = useState(false);
  const [liveOffline, setLiveOffline] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const cannedRef = useRef<HTMLDivElement>(null);
  const transferRef = useRef<HTMLDivElement>(null);

  // The wait figures are the point of the screen, so they tick. One timer for
  // the page, not one per row.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const rows = useMemo(() => {
    const today = startOfToday(now);
    return [...sessions]
      .filter((s) => inCohort(s, cohort, user?.id, seesAll, agentFilter || undefined))
      // "Closed today" means today: the list endpoint returns the latest closes of any day.
      .filter((s) => cohort !== 'closed' || (s.endedAt ? new Date(s.endedAt).getTime() >= today : false))
      .sort((a, b) => byWait(a, b, now));
    // `now` reorders the list as waits grow; that is the intended behaviour.
  }, [sessions, cohort, user?.id, seesAll, agentFilter, now]);

  const agentNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const a of agents) names[a.id] = memberName(a);
    return names;
  }, [agents]);

  const subtitle = useMemo(
    () =>
      consoleSubtitle(
        rows,
        stats && { waiting: stats.waiting, active: seesAll ? stats.active : stats.mine?.active ?? 0 },
        now,
      ),
    [rows, stats, seesAll, now],
  );

  const cohortCount = useCallback(
    (key: ChatCohort): number | null => {
      if (!stats) return null;
      if (key === 'waiting') return stats.waiting;
      if (key === 'bot') return stats.bot ?? null;
      if (key === 'closed') return seesAll ? stats.endedToday : stats.mine?.endedToday ?? null;
      return seesAll ? stats.active : stats.mine?.active ?? null;
    },
    [stats, seesAll],
  );

  // The AI's own account of what happened before it handed over, if it did.
  const handoff = useMemo(() => {
    const m = selectedSession?.metadata;
    if (!m) return null;
    const summary =
      (m.comprehensiveSummary as Record<string, any> | undefined)?.summary ||
      (typeof m.conversationSummary === 'string' ? m.conversationSummary : undefined);
    const reason = typeof m.handoffReason === 'string' ? m.handoffReason : undefined;
    return summary || reason ? { summary, reason } : null;
  }, [selectedSession]);

  const linkedTicketNumber = useMemo(() => {
    const systemMsg = [...messages]
      .reverse()
      .find((m) => m.senderType === SenderType.SYSTEM && m.content?.includes('Ticket #'));
    const match = systemMsg?.content.match(/Ticket #([\w-]+)/);
    return match ? match[1] : null;
  }, [messages]);

  const fetchStats = useCallback(async () => {
    try {
      setStats(await chatService.getStats());
    } catch (err) {
      console.error('Failed to fetch chat stats:', err);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      let data: ChatSession[];
      switch (cohort) {
        case 'waiting':
          data = await chatService.getWaitingSessions();
          break;
        case 'active':
          // An agent's own chats; an admin's view of everyone's.
          data = seesAll
            ? (await chatService.getSessions({ status: ChatSessionStatus.ACTIVE, limit: 100 })).data
            : user?.id
              ? await chatService.getAgentSessions(user.id)
              : [];
          break;
        case 'bot':
          data = seesAll
            ? (await chatService.getSessions({ status: ChatSessionStatus.BOT, limit: 100 })).data
            : [];
          break;
        default:
          // The server returns an agent only the chats they handled.
          data = (await chatService.getSessions({ status: ChatSessionStatus.ENDED, limit: 100 })).data;
      }
      setSessions(data);
    } catch (err: any) {
      setError(apiMessage(err, 'Failed to load conversations'));
    } finally {
      setIsLoading(false);
    }
  }, [cohort, user?.id, seesAll]);

  /** Close the open chat with a reason, e.g. when it turns out to be someone else's. */
  const dropSelection = useCallback((why: string | null) => {
    setSelectedSession(null);
    selectedIdRef.current = null;
    setMessages([]);
    if (why) setNotice(why);
  }, []);

  const fetchMessages = useCallback(async (sessionId: string) => {
    try {
      const data = await chatService.getMessages(sessionId);
      // A slow answer for a chat no longer open must not land in this one.
      if (selectedIdRef.current !== sessionId) return;
      setMessages((prev) => {
        if (prev.length === 0 || data.length !== prev.length) return data;
        if (prev[prev.length - 1]?.id !== data[data.length - 1]?.id) return data;
        return prev;
      });
    } catch (err: any) {
      if (err?.response?.status === 403 && selectedIdRef.current === sessionId) {
        dropSelection(apiMessage(err, 'This chat is being handled by another agent.'));
      } else {
        console.error('Failed to fetch messages:', err);
      }
    } finally {
      setIsLoadingMessages(false);
    }
  }, [dropSelection]);

  const { joinSession, leaveSession, isConnected } = useChatWebSocket({
    onNewSession: (session) => {
      if (cohort === 'waiting' && session?.status === ChatSessionStatus.WAITING) {
        setSessions((prev) => (prev.some((s) => s.id === session.id) ? prev : [session, ...prev]));
      }
      fetchStats();
    },
    // Accepted by someone: it leaves this queue at once, and a colleague's
    // chat closes here unless this is an admin.
    onSessionTaken: ({ sessionId, agentId }) => {
      if (agentId !== user?.id) {
        if (!seesAll) setSessions((prev) => prev.filter((s) => s.id !== sessionId));
        if (!seesAll && selectedIdRef.current === sessionId) {
          dropSelection(`${agentNames[agentId] || 'Another agent'} accepted that chat.`);
        }
      }
      fetchSessions();
      fetchStats();
    },
    onChatTransferred: (session) => {
      setNotice(`${visitorName(session)}'s chat was transferred to you.`);
      fetchSessions();
      fetchStats();
    },
    onMessage: ({ sessionId, message }) => {
      // Agent messages are added optimistically when sent.
      if (message.senderType === SenderType.AGENT) return;
      if (selectedSession?.id === sessionId) {
        setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      }
    },
    onSessionEnded: ({ sessionId }) => {
      // The customer-end path emits the conversationId, which may differ from
      // the agent-side session.id, so match on either (#9687).
      if (
        selectedSession &&
        (selectedSession.id === sessionId || selectedSession.conversationId === sessionId)
      ) {
        setSelectedSession((prev) => (prev ? { ...prev, status: ChatSessionStatus.ENDED } : null));
      }
      fetchSessions();
      fetchStats();
    },
  });

  useEffect(() => {
    fetchStats();
    fetchSessions();
    const interval = setInterval(() => {
      fetchStats();
      fetchSessions();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchStats, fetchSessions]);

  // Availability is READ-ONLY here: the router owns it and there is no setter
  // on the API. Stating it is still worth doing — it answers "will the next
  // chat come to me?" — but it is not dressed up as a control.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    chatService
      .getAgentStatus(user.id)
      .then((s) => !cancelled && setAvailable(Boolean(s?.online)))
      .catch(() => !cancelled && setAvailable(null));
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!selectedSession) return;
    setIsLoadingMessages(true);
    fetchMessages(selectedSession.id);
    const interval = setInterval(() => fetchMessages(selectedSession.id), 5000);
    return () => clearInterval(interval);
  }, [selectedSession, fetchMessages]);

  // Follow the conversation only while the agent is at the end of it. Scrolled
  // up reading history, a new message must not yank them down: it raises the
  // "New messages" button instead. Opening a chat, or sending, always goes to
  // the end.
  useEffect(() => {
    if (messages.length === 0) return;
    const openedNow = scrolledSessionRef.current !== selectedIdRef.current;
    const last = messages[messages.length - 1];
    if (openedNow || atBottomRef.current || last?.senderType === SenderType.AGENT) {
      scrolledSessionRef.current = selectedIdRef.current;
      messagesEndRef.current?.scrollIntoView({ behavior: openedNow ? 'auto' : 'smooth', block: 'end' });
      setUnseen(false);
    } else {
      setUnseen(true);
    }
  }, [messages]);

  const onTranscriptScroll = () => {
    const el = transcriptRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    if (atBottomRef.current) setUnseen(false);
  };

  const jumpToLatest = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    atBottomRef.current = true;
    setUnseen(false);
  };

  // Say so when live updates have been down for a moment. The page keeps
  // polling, so nothing is lost — it is only slower.
  useEffect(() => {
    if (isConnected) {
      setLiveOffline(false);
      return;
    }
    const t = setTimeout(() => setLiveOffline(true), OFFLINE_GRACE_MS);
    return () => clearTimeout(t);
  }, [isConnected]);

  // A new chat starts with a clean composer state.
  useEffect(() => {
    setSendError(null);
    setUnseen(false);
    atBottomRef.current = true;
  }, [selectedSession?.id]);

  // What else this account already has open. The rail exists so an agent
  // notices that the chat and a breached ticket are the same problem.
  useEffect(() => {
    const s = selectedSession;
    const email = typeof s?.metadata?.visitorEmail === 'string' ? s.metadata.visitorEmail : undefined;
    if (!s || (!s.customerId && !email)) {
      setOpenTickets(null);
      return;
    }
    let cancelled = false;
    setOpenTickets(null);
    ticketService
      .getTickets({
        ...(s.customerId ? { customerId: s.customerId } : { contactEmail: email }),
        status: ['new', 'open', 'in_progress', 'pending'],
        limit: 5,
        sortBy: 'created_at',
        sortOrder: 'desc',
      })
      .then((res) => !cancelled && setOpenTickets(res.data ?? []))
      .catch(() => !cancelled && setOpenTickets([]));
    return () => {
      cancelled = true;
    };
  }, [selectedSession]);

  useEffect(() => {
    cannedResponsesService
      .getAll({ isActive: true })
      .then(setCanned)
      .catch(() => setCanned([]));
    teamMembersService
      .getTeamMembers({ status: 'active', limit: 100 })
      .then((r) => setAgents(r.data ?? []))
      .catch(() => setAgents([]));
  }, []);

  // Close the two popovers on an outside click.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (cannedRef.current && !cannedRef.current.contains(e.target as Node)) setShowCanned(false);
      if (transferRef.current && !transferRef.current.contains(e.target as Node))
        setShowTransfer(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const handleSelectSession = (session: ChatSession) => {
    if (selectedSession) leaveSession(selectedSession.id);
    setSelectedSession(session);
    selectedIdRef.current = session.id;
    setMessages([]);
    joinSession(session.id);
  };

  const handleAcceptChat = async (session: ChatSession) => {
    if (!user?.id) {
      setError('You are not signed in as an agent — sign out and back in to accept chats.');
      return;
    }
    try {
      const updated = await chatService.assignAgent(session.id, { agentId: user.id });
      if (selectedSession && selectedSession.id !== session.id) leaveSession(selectedSession.id);
      setSelectedSession(updated);
      selectedIdRef.current = updated.id;
      joinSession(session.id);
      setCohort('active');
      fetchSessions();
      fetchStats();
    } catch (err: any) {
      // 409: another agent accepted it a moment earlier.
      if (err?.response?.status === 409 || err?.response?.status === 403) {
        dropSelection(apiMessage(err, 'Another agent accepted that chat.'));
        fetchSessions();
        fetchStats();
      } else {
        setError(apiMessage(err, 'Failed to accept the chat'));
      }
    }
  };

  const handleTransfer = async (targetAgentId: string) => {
    if (!selectedSession || !user?.id) return;
    try {
      setIsTransferring(true);
      await chatService.transferChat(selectedSession.id, { targetAgentId }, user.id);
      setShowTransfer(false);
      leaveSession(selectedSession.id);
      dropSelection(`Transferred to ${agentNames[targetAgentId] || 'the agent'}.`);
      fetchSessions();
      fetchStats();
    } catch (err: any) {
      setError(apiMessage(err, 'Failed to transfer the chat'));
    } finally {
      setIsTransferring(false);
    }
  };

  const handleConfirmEndChat = async (options: {
    createTicket: boolean;
    ticketSubject: string;
    ticketStatus: 'new' | 'resolved';
  }) => {
    if (!selectedSession) return;
    setIsEndingChat(true);
    try {
      const result = await chatService.endSession(selectedSession.id, {
        reason: 'Ended by agent',
        createTicket: options.createTicket,
        ticketSubject: options.ticketSubject || undefined,
        ticketStatus: options.createTicket ? options.ticketStatus : undefined,
        assigneeId:
          options.createTicket && options.ticketStatus === 'resolved' ? user?.id : undefined,
      });
      leaveSession(selectedSession.id);
      dropSelection(null);
      setShowEndChatModal(false);
      fetchSessions();
      fetchStats();
      if (options.createTicket && result?.ticketId) navigate(`/tickets/${result.ticketId}`);
    } catch (err: any) {
      setError(apiMessage(err, 'Failed to end the chat'));
    } finally {
      setIsEndingChat(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedSession || !messageInput.trim() || !user?.id || sendingRef.current) return;
    sendingRef.current = true;
    const content = messageInput.trim();
    setMessageInput('');
    setSendError(null);
    const tempId = `temp-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: tempId,
        sessionId: selectedSession.id,
        content,
        type: MessageType.TEXT,
        senderType: SenderType.AGENT,
        senderId: user.id,
        senderName: user.name || 'Agent',
        createdAt: new Date().toISOString(),
      },
    ]);
    try {
      setIsSending(true);
      const sent = await chatService.sendMessage(selectedSession.id, {
        content,
        senderType: SenderType.AGENT,
        senderId: user.id,
      });
      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...sent, senderName: user.name || 'Agent' } : m)),
      );
    } catch (err: any) {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      const msg = String(err?.response?.data?.message || err?.message || '');
      const ended =
        err?.response?.status === 409 || /ended|closed|not active|inactive|no longer/i.test(msg);
      if (ended) {
        // Already closed — reflect that instead of erroring on every send (#9687).
        setSelectedSession((prev) => (prev ? { ...prev, status: ChatSessionStatus.ENDED } : null));
      } else {
        // Said where the agent is looking, with their text handed back.
        setSendError(msg || 'The message could not be sent.');
        setMessageInput(content);
      }
    } finally {
      sendingRef.current = false;
      setIsSending(false);
      composerRef.current?.focus();
    }
  };

  const selectedWait = selectedSession ? waitSeconds(selectedSession, now) : null;
  // Who may reply: the agent holding the chat. An admin reading a colleague's
  // chat transfers it to themselves first, so two people never answer at once.
  const holdsSelected = !!selectedSession && !!user?.id && selectedSession.agentId === user.id;
  const mayManageSelected = !!selectedSession && (holdsSelected || seesAll);

  return (
    <div className="flex h-full min-h-0 flex-col gap-[14px] overflow-hidden bg-[var(--co-canvas)] py-4 pr-4">
      {/* ---------- header card ---------- */}
      <header className={clsx(CARD_CLASS, 'flex-none px-5 py-[18px]')} style={{ ...CARD, borderRadius: 14 }}>
        {/* Row 1: title + subtitle, the utility cluster in the top-right corner. */}
        <div className="flex items-center justify-between gap-[10px]">
          <div className="min-w-0">
            <h1 className="text-[22px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)] sm:text-[28px]">
              Live Chat
            </h1>
            <p className="co-mono mt-[6px] text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
              {subtitle}
            </p>
          </div>
          <div className="flex h-[34px] flex-none items-center">
            <PageHeaderMeta />
          </div>
        </div>

        {/* Row 2: cohort tabs on the left, every other header control on the right. */}
        <div className="mt-3 flex flex-wrap items-center gap-[10px] border-t border-[var(--co-line-subtle)] pt-3">
            <div className="flex items-center gap-[6px] rounded-[10px] bg-[var(--co-selected)] p-1">
              {cohorts.map((c) => (
                <CohortTab
                  key={c.key}
                  label={c.label}
                  count={cohortCount(c.key)}
                  active={cohort === c.key}
                  onClick={() => setCohort(c.key)}
                />
              ))}
            </div>
            {seesAll && (cohort === 'active' || cohort === 'closed') && (
              <select
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
                aria-label="Show one agent's chats"
                className="h-[34px] rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-2 text-[12.5px] text-[var(--co-ink-2)]"
              >
                <option value="">All agents</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {memberName(a)}
                  </option>
                ))}
              </select>
            )}
            {notice && (
              <span role="status" className="text-[12px] text-[var(--co-ink-2)]">
                {notice}{' '}
                <button type="button" onClick={() => setNotice(null)} className="underline underline-offset-2">
                  dismiss
                </button>
              </span>
            )}
            {error && (
              <span role="alert" className="text-[12px] text-[var(--co-burgundy)]">
                {error}{' '}
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="underline underline-offset-2"
                >
                  dismiss
                </button>
              </span>
            )}
            <div className="flex-1" />
            {liveOffline && (
              <span
                role="status"
                title="New chats and messages still arrive, a few seconds later than usual."
                className="co-mono flex items-center gap-[6px] rounded-[10px] border px-2 py-[7px] text-[9.5px] font-semibold uppercase tracking-[.11em]"
                style={{
                  borderColor: 'var(--co-terracotta-line)',
                  background: 'var(--co-terracotta-wash)',
                  color: 'var(--co-terracotta)',
                }}
              >
                <RefreshCw aria-hidden className="h-[11px] w-[11px] animate-spin" />
                Reconnecting live updates
              </span>
            )}
            {available !== null && (
              <span
                title={
                  available
                    ? 'You have a live connection to the chat service, so new chats can be routed to you.'
                    : 'New chats are not routed to you: this browser has no live connection to the chat service. Refresh the page; if it stays like this, the live connection is being blocked.'
                }
                className="co-mono flex items-center gap-[6px] rounded-[10px] border px-2 py-[7px] text-[9.5px] font-semibold uppercase tracking-[.11em]"
                style={
                  available
                    ? {
                        borderColor: 'var(--co-green-line)',
                        background: 'var(--co-green-wash)',
                        color: 'var(--co-green)',
                      }
                    : {
                        borderColor: 'var(--co-line-strong)',
                        background: 'var(--co-selected)',
                        color: 'var(--co-ink-3)',
                      }
                }
              >
                {available ? 'Receiving chats' : 'Not receiving · no live connection'}
              </span>
            )}

            <button
              type="button"
              onClick={() => {
                fetchStats();
                fetchSessions();
              }}
              disabled={isLoading}
              className={HEADER_CONTROL}
            >
              <RefreshCw className={clsx('h-[14px] w-[14px]', isLoading && 'animate-spin')} />
              Refresh
            </button>
        </div>
      </header>

      <InsightStrip stats={stats} seesAll={seesAll} />

      {/* ---------- conversations · transcript · visitor ---------- */}
      <div className="flex min-h-0 flex-1 gap-[14px]">
        {/* Below lg the list and the open chat take turns; the visitor rail
            needs xl. Nothing is dropped — the header carries the visitor's
            email and the chat's state. */}
        <section
          aria-label="Conversations"
          className={clsx(
            CARD_CLASS,
            'w-full flex-col overflow-hidden lg:flex lg:w-[300px] lg:flex-none xl:w-[346px]',
            selectedSession ? 'hidden' : 'flex',
          )}
          style={CARD}
        >
          <div className="co-mono flex flex-none items-center border-b border-[var(--co-line)] px-5 pb-[11px] pt-[14px] text-[9.5px] font-semibold uppercase tracking-[.2em] text-[var(--co-ink-3)]">
            <div className="min-w-0 flex-1">Conversations</div>
            {/* the arrow sits on the column the rows are actually ordered by */}
            <div className="w-[70px] flex-none text-right text-[var(--co-ink)]">Wait ↓</div>
          </div>

          <div className="co-scrollbar-none min-h-0 flex-1 overflow-y-auto">
            {isLoading && rows.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="h-6 w-6 animate-spin text-[var(--co-ink-3)]" />
              </div>
            ) : rows.length === 0 ? (
              <p className="px-5 py-12 text-[13px] text-[var(--co-ink-3)]">
                {cohort === 'waiting'
                  ? 'Nobody is waiting. Every conversation has someone on it.'
                  : cohort === 'active'
                    ? seesAll
                      ? 'No agent is in a chat right now.'
                      : 'You are not in a chat. Accept one from Waiting.'
                    : cohort === 'bot'
                      ? 'The assistant is not holding any conversation.'
                      : seesAll
                        ? 'No chat has closed today.'
                        : 'You have not closed a chat today.'}
              </p>
            ) : (
              rows.map((s) => (
                <ConversationRow
                  key={s.id}
                  session={s}
                  now={now}
                  myAgentId={user?.id}
                  agentNames={agentNames}
                  selected={s.id === selectedSession?.id}
                  onSelect={handleSelectSession}
                />
              ))
            )}
          </div>

          <div
            className="flex flex-none flex-wrap items-center gap-[14px] border-t border-[var(--co-line)] bg-[var(--co-selected)] px-5 py-3"
            style={{ borderRadius: '0 0 9px 9px' }}
          >
            <span className="co-mono whitespace-nowrap text-[10.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
              {rows.length} {rows.length === 1 ? 'conversation' : 'conversations'}
            </span>
            <div className="flex-1" />
            <span className="text-[12px] text-[var(--co-ink-3)]">Sorted by wait, not recency.</span>
          </div>
        </section>

        <section
          aria-label="Conversation"
          className={clsx(
            CARD_CLASS,
            'min-w-0 flex-1 flex-col overflow-hidden lg:flex',
            selectedSession ? 'flex' : 'hidden',
          )}
          style={CARD}
        >
          {!selectedSession ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-8 text-center">
              <MessageCircle className="h-10 w-10 text-[var(--co-ink-4)]" />
              <p className="text-[14px] font-semibold text-[var(--co-ink)]">No conversation open</p>
              <p className="max-w-[46ch] text-[12.5px] leading-[1.6] text-[var(--co-ink-3)]">
                {rows.length > 0
                  ? 'Pick the row at the top of the list — it is the one that has been waiting longest.'
                  : 'There is nothing to open yet. New conversations appear in the list as visitors start chatting.'}
              </p>
            </div>
          ) : (
            <>
              <div className="flex flex-none flex-wrap items-center gap-x-[14px] gap-y-3 border-b border-[var(--co-line-subtle)] px-4 pb-[14px] pt-4 sm:px-5">
                <button
                  type="button"
                  onClick={() => {
                    leaveSession(selectedSession.id);
                    dropSelection(null);
                  }}
                  aria-label="Back to conversations"
                  className="-ml-1 flex h-[34px] w-[34px] flex-none items-center justify-center rounded-lg text-[var(--co-ink-2)] hover:bg-[var(--co-selected)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--co-brass)] lg:hidden"
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden />
                </button>
                <div
                  aria-hidden
                  className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[7px] bg-[var(--co-ink-2)] text-[14px] font-semibold text-[var(--co-ink-invert)]"
                >
                  {initial(visitorName(selectedSession))}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <h2 className="truncate text-[17px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)] sm:text-[19px]">
                      {visitorName(selectedSession)}
                    </h2>
                    {(() => {
                      const st = sessionState(selectedSession, holdsSelected, agentNames);
                      return (
                        <span
                          className="co-mono flex-none whitespace-nowrap rounded-[5px] border px-[7px] py-[2px] text-[9px] font-semibold uppercase tracking-[.1em]"
                          style={{ borderColor: st.line, background: st.wash, color: st.color }}
                        >
                          {st.label}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="co-mono mt-1 truncate text-[10px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
                    {[
                      typeof selectedSession.metadata?.visitorEmail === 'string' &&
                      selectedSession.metadata.visitorEmail
                        ? selectedSession.metadata.visitorEmail
                        : 'No email given',
                      (selectedSession.channel || 'web chat').replace(/_/g, ' '),
                      selectedSession.status === ChatSessionStatus.ENDED
                        ? 'ended'
                        : selectedWait !== null
                          ? `waiting ${waitClock(selectedWait)}`
                          : 'answered',
                    ].join(' · ')}
                  </div>
                </div>

                <div className="ml-auto flex flex-none flex-wrap items-center gap-2">
                  {selectedWait !== null && waitTone(selectedWait) === 'danger' && (
                    <span
                      className="co-mono whitespace-nowrap rounded-[5px] border px-2 py-[3px] text-[9.5px] font-semibold uppercase tracking-[.1em]"
                      style={{
                        borderColor: 'var(--co-terracotta-line)',
                        background: 'var(--co-terracotta-wash)',
                        color: 'var(--co-terracotta)',
                      }}
                    >
                      Waiting {waitClock(selectedWait)}
                    </span>
                  )}

                  {selectedSession.status === ChatSessionStatus.WAITING && (
                    <button
                      type="button"
                      onClick={() => handleAcceptChat(selectedSession)}
                      className={PRIMARY}
                    >
                      Accept chat
                    </button>
                  )}

                  {selectedSession.status === ChatSessionStatus.ACTIVE && mayManageSelected && (
                    <>
                      <div className="relative" ref={transferRef}>
                        <button
                          type="button"
                          onClick={() => setShowTransfer((v) => !v)}
                          aria-haspopup="menu"
                          aria-expanded={showTransfer}
                          className={CONTROL}
                        >
                          Transfer
                        </button>
                        {showTransfer && (
                          <div
                            className={clsx(
                              CARD_CLASS,
                              'absolute right-0 top-full z-50 mt-2 w-[260px] overflow-hidden',
                            )}
                            style={{ borderRadius: 10, boxShadow: 'var(--co-shadow-pop)' }}
                          >
                            <div className="co-mono border-b border-[var(--co-line-subtle)] px-4 py-3 text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
                              Hand to
                            </div>
                            <div className="co-scrollbar-none max-h-[260px] overflow-y-auto">
                              {agents.filter((a) => a.id !== selectedSession.agentId).length === 0 ? (
                                <p className="px-4 py-5 text-[12px] text-[var(--co-ink-3)]">
                                  No other active agent to hand this to.
                                </p>
                              ) : (
                                agents
                                  // Anyone but the agent holding it - for an admin, possibly themselves.
                                  .filter((a) => a.id !== selectedSession.agentId)
                                  .map((a) => (
                                    <button
                                      key={a.id}
                                      type="button"
                                      disabled={isTransferring}
                                      onClick={() => handleTransfer(a.id)}
                                      className="flex w-full items-center justify-between gap-3 px-4 py-[10px] text-left hover:bg-[var(--co-selected)] disabled:opacity-50"
                                    >
                                      <span className="truncate text-[12.5px] text-[var(--co-ink)]">
                                        {a.id === user?.id ? `${memberName(a)} (you)` : memberName(a)}
                                      </span>
                                      <span className="co-mono flex-none text-[9.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)]">
                                        {a.role}
                                      </span>
                                    </button>
                                  ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => setShowEndChatModal(true)}
                        className={CONTROL}
                      >
                        End chat
                      </button>
                    </>
                  )}
                </div>
              </div>

              {handoff && (
                <div
                  className="flex-none border-b px-5 py-[13px]"
                  style={{
                    borderColor: 'var(--co-machine-line)',
                    background: 'var(--co-machine-wash)',
                  }}
                >
                  <div
                    className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em]"
                    style={{ color: 'var(--co-machine-ink)' }}
                  >
                    Handed over by the assistant · unreviewed
                  </div>
                  {handoff.summary && (
                    <p className="mt-[9px] max-w-[86ch] text-[13px] leading-[1.6] text-[var(--co-ink-2)]">
                      {handoff.summary}
                    </p>
                  )}
                  {handoff.reason && (
                    <p className="mt-[7px] text-[12px] leading-[1.55] text-[var(--co-ink-3)]">
                      Reason given: {handoff.reason}
                    </p>
                  )}
                </div>
              )}

              <div className="relative flex min-h-0 flex-1 flex-col">
                <div
                  ref={transcriptRef}
                  onScroll={onTranscriptScroll}
                  role="log"
                  aria-live="polite"
                  aria-label={`Conversation with ${visitorName(selectedSession)}`}
                  aria-busy={isLoadingMessages && messages.length === 0}
                  className="co-scrollbar-none flex min-h-0 flex-1 flex-col gap-[13px] overflow-y-auto px-4 py-[18px] sm:px-5"
                >
                  {isLoadingMessages && messages.length === 0 ? (
                    <div className="flex items-center justify-center gap-2 py-16 text-[12.5px] text-[var(--co-ink-3)]">
                      <RefreshCw aria-hidden className="h-5 w-5 animate-spin" />
                      Loading the conversation…
                    </div>
                  ) : messages.length === 0 ? (
                    <p className="py-12 text-center text-[13px] text-[var(--co-ink-3)]">
                      Nothing said yet.
                    </p>
                  ) : (
                    messages.map((m, i) => (
                      <div key={m.id} className="flex flex-col gap-[13px]">
                        {startsDay(m, messages[i - 1]) && (
                          <div className="flex items-center gap-3 pt-1" role="separator" aria-label={dayLabel(m.createdAt, now)}>
                            <span className="h-px flex-1 bg-[var(--co-line-subtle)]" />
                            <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.14em] text-[var(--co-ink-3)]">
                              {dayLabel(m.createdAt, now)}
                            </span>
                            <span className="h-px flex-1 bg-[var(--co-line-subtle)]" />
                          </div>
                        )}
                        <Bubble message={m} session={selectedSession} />
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
                {unseen && (
                  <button
                    type="button"
                    onClick={jumpToLatest}
                    className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-[6px] rounded-full bg-[var(--co-brass-ink)] px-[14px] py-[7px] text-[12px] font-semibold text-[var(--co-brass-on)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--co-brass)] focus-visible:ring-offset-2"
                    style={{ boxShadow: 'var(--co-shadow-pop)' }}
                  >
                    <ArrowDown aria-hidden className="h-[14px] w-[14px]" />
                    New messages
                  </button>
                )}
              </div>

              {selectedSession.status === ChatSessionStatus.ACTIVE && holdsSelected ? (
                <div
                  className="flex-none border-t border-[var(--co-line)] bg-[var(--co-selected)] px-5 py-[14px]"
                  style={{ borderRadius: '0 0 9px 9px' }}
                >
                  {sendError && (
                    <div
                      role="alert"
                      className="mb-[10px] flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-[12px] leading-[1.45]"
                      style={{
                        borderColor: 'var(--co-burgundy-line)',
                        background: 'var(--co-burgundy-wash)',
                        color: 'var(--co-burgundy)',
                      }}
                    >
                      <span className="min-w-0 flex-1">
                        Not sent — {sendError.replace(/\.?\s*$/, '.')} Your text is back in the box.
                      </span>
                      <button
                        type="button"
                        onClick={handleSendMessage}
                        disabled={isSending || !messageInput.trim()}
                        className="font-semibold underline underline-offset-2 disabled:opacity-50"
                      >
                        Try again
                      </button>
                      <button
                        type="button"
                        onClick={() => setSendError(null)}
                        aria-label="Dismiss"
                        className="flex-none"
                      >
                        <X aria-hidden className="h-[13px] w-[13px]" />
                      </button>
                    </div>
                  )}
                  <textarea
                    ref={composerRef}
                    aria-label="Reply to the visitor"
                    aria-describedby="live-chat-send-hint"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    rows={2}
                    placeholder="Type a reply…"
                    className="max-h-[40vh] min-h-[64px] w-full resize-y break-words rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-[14px] py-[11px] text-[13px] leading-[1.55] text-[var(--co-ink)] placeholder:text-[var(--co-ink-3)] focus:border-[var(--co-brass)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--co-brass-wash)]"
                  />
                  <div className="mt-[11px] flex flex-wrap items-center gap-[9px]">
                    <div className="relative" ref={cannedRef}>
                      <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={showCanned}
                        onClick={() => setShowCanned((v) => !v)}
                        disabled={canned.length === 0}
                        title={
                          canned.length === 0
                            ? 'No canned replies are saved for this workspace.'
                            : undefined
                        }
                        className={CONTROL}
                      >
                        Canned ⌄
                      </button>
                      {showCanned && canned.length > 0 && (
                        <div
                          className={clsx(
                            CARD_CLASS,
                            'absolute bottom-full left-0 z-50 mb-2 w-[340px] overflow-hidden',
                          )}
                          style={{ borderRadius: 10, boxShadow: 'var(--co-shadow-pop)' }}
                        >
                          <div className="co-scrollbar-none max-h-[300px] overflow-y-auto">
                            {canned.map((c) => (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => {
                                  setMessageInput((prev) =>
                                    prev ? `${prev}\n${c.content}` : c.content,
                                  );
                                  setShowCanned(false);
                                  cannedResponsesService.incrementUsage(c.id).catch(() => {});
                                }}
                                className="block w-full border-b border-[var(--co-line-subtle)] px-4 py-3 text-left last:border-b-0 hover:bg-[var(--co-selected)]"
                              >
                                <div className="flex items-baseline gap-2">
                                  <span className="truncate text-[12.5px] font-semibold text-[var(--co-ink)]">
                                    {c.name}
                                  </span>
                                  {c.shortcut && (
                                    <span className="co-mono flex-none text-[10px] text-[var(--co-ink-3)]">
                                      /{c.shortcut}
                                    </span>
                                  )}
                                </div>
                                <p className="mt-1 line-clamp-2 text-[11.5px] leading-[1.5] text-[var(--co-ink-3)]">
                                  {c.content}
                                </p>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <span
                      id="live-chat-send-hint"
                      className="co-mono hidden text-[9.5px] uppercase tracking-[.08em] text-[var(--co-ink-3)] sm:inline"
                    >
                      {SEND_KEYS} to send
                    </span>

                    <div className="flex-1" />

                    {selectedWait !== null && (
                      <span
                        className="co-mono text-[9.5px] uppercase tracking-[.08em]"
                        style={{ color: WAIT_COLOR[waitTone(selectedWait)] }}
                      >
                        Visitor waiting {waitClock(selectedWait)}
                      </span>
                    )}

                    <button
                      type="button"
                      onClick={handleSendMessage}
                      disabled={!messageInput.trim() || isSending}
                      aria-busy={isSending}
                      className={PRIMARY}
                    >
                      {isSending ? (
                        <>
                          <RefreshCw aria-hidden className="mr-[6px] h-[13px] w-[13px] animate-spin" />
                          Sending…
                        </>
                      ) : (
                        'Send'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="flex-none border-t border-[var(--co-line)] bg-[var(--co-selected)] px-5 py-[14px] text-[12.5px] text-[var(--co-ink-3)]"
                  style={{ borderRadius: '0 0 9px 9px' }}
                >
                  {selectedSession.status === ChatSessionStatus.WAITING
                    ? 'Accept the chat before replying — the visitor is not connected to you yet.'
                    : selectedSession.status === ChatSessionStatus.BOT
                      ? 'The assistant is handling this chat. It joins the queue if the visitor asks for a person.'
                      : selectedSession.status === ChatSessionStatus.ACTIVE
                        ? `${agentNames[selectedSession.agentId ?? ''] || 'Another agent'} is handling this chat. Transfer it to yourself to reply.`
                        : 'This chat has ended. Nothing more can be sent on it.'}
                </div>
              )}
            </>
          )}
        </section>

        <aside aria-label="Visitor and tickets" className="hidden w-[312px] flex-none flex-col gap-[14px] xl:flex">
          {seesAll && !selectedSession ? (
            <TeamCard
              stats={stats}
              agents={agents}
              onPick={(agentId) => {
                setAgentFilter(agentId);
                setCohort('active');
              }}
            />
          ) : (
          <>
          <VisitorCard
            session={selectedSession}
            messages={messages}
            agentNames={agentNames}
            myAgentId={user?.id}
            now={now}
          />
          <OpenTicketsCard
            session={selectedSession}
            tickets={openTickets}
            linkedTicketNumber={linkedTicketNumber}
            now={now}
            onOpen={(id) => navigate(`/tickets/${id}`)}
          />
          </>
          )}
        </aside>
      </div>

      {showEndChatModal && selectedSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6">
          <EndChatModal
            onConfirm={handleConfirmEndChat}
            onCancel={() => setShowEndChatModal(false)}
            isLoading={isEndingChat}
            firstCustomerMessage={
              messages
                .find(
                  (m) =>
                    m.senderType === SenderType.VISITOR || m.senderType === SenderType.CUSTOMER,
                )
                ?.content?.slice(0, 80)
            }
          />
        </div>
      )}
    </div>
  );
}

function CohortTab({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number | null;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="co-mono flex items-center gap-[6px] rounded-lg px-3 py-[7px] text-[10.5px] uppercase tracking-[.12em]"
      style={
        active
          ? {
              background: 'var(--co-raised)',
              color: 'var(--co-ink)',
              fontWeight: 600,
              boxShadow: 'var(--co-shadow)',
            }
          : { color: 'var(--co-ink-3)', fontWeight: 500 }
      }
    >
      {label}
      {count !== null && (
        <span
          className="font-semibold"
          style={{ color: active ? 'var(--co-brass-ink)' : 'var(--co-ink-3)' }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function ConversationRow({
  session,
  now,
  myAgentId,
  agentNames,
  selected,
  onSelect,
}: {
  session: ChatSession;
  now: number;
  myAgentId?: string;
  agentNames?: Record<string, string>;
  selected: boolean;
  onSelect: (s: ChatSession) => void;
}) {
  const wait = waitSeconds(session, now);
  const tone = waitTone(wait);
  const owner = ownerLabel(session, myAgentId, agentNames);
  const isBot = isBotHandled(session);

  return (
    <button
      type="button"
      onClick={() => onSelect(session)}
      aria-current={selected ? 'true' : undefined}
      className="flex w-full gap-[11px] border-b border-[var(--co-line-subtle)] px-4 py-3 text-left hover:bg-[var(--co-selected)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--co-brass)]"
      style={{
        borderLeft: `2px solid ${
          selected ? 'var(--co-brass)' : tone === 'danger' ? 'var(--co-terracotta)' : 'transparent'
        }`,
        background: selected ? 'var(--co-brass-wash)' : undefined,
      }}
    >
      <div
        className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[7px] text-[10px] font-semibold"
        style={
          isBot
            ? {
                border: '1px solid var(--co-machine-line)',
                background: 'var(--co-machine-wash)',
                color: 'var(--co-machine-ink)',
                fontFamily: 'var(--co-font-mono)',
                fontSize: 8.5,
              }
            : { background: 'var(--co-ink-2)', color: 'var(--co-ink-invert)' }
        }
      >
        {isBot ? 'AI' : initial(visitorName(session))}
      </div>

      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold text-[var(--co-ink)]">
          {visitorName(session)}
        </div>
        <div className="mt-1 truncate text-[11.5px] text-[var(--co-ink-3)]">{lastLine(session)}</div>
      </div>

      <div className="w-[70px] flex-none text-right">
        <div
          className="co-mono text-[11.5px] font-semibold tabular-nums"
          style={{ color: WAIT_COLOR[tone] }}
        >
          {waitClock(wait)}
        </div>
        <div
          className="co-mono mt-1 text-[9px] uppercase tracking-[.08em]"
          style={{
            color:
              owner === 'waiting'
                ? 'var(--co-terracotta)'
                : owner === 'bot'
                  ? 'var(--co-machine-ink)'
                  : 'var(--co-ink-3)',
          }}
        >
          {owner}
        </div>
      </div>
    </button>
  );
}

function Bubble({ message, session }: { message: ChatMessage; session: ChatSession }) {
  const mine = message.senderType === SenderType.AGENT;
  const isBot = message.senderType === SenderType.BOT;

  if (message.senderType === SenderType.SYSTEM) {
    return (
      <p className="co-mono mx-auto max-w-[90%] rounded-[10px] bg-[var(--co-selected)] px-3 py-1 text-center text-[9.5px] uppercase tracking-[.1em] text-[var(--co-ink-3)] [overflow-wrap:anywhere]">
        {message.content}
      </p>
    );
  }

  const who = mine ? 'You' : isBot ? 'Assistant' : message.senderName || visitorName(session);
  // Added optimistically on send; replaced by the stored message when it lands.
  const pending = mine && message.id.startsWith('temp-');

  return (
    <div className={clsx('flex', mine && 'justify-end')}>
      <div className={clsx('min-w-0 max-w-[88%] sm:max-w-[75%] xl:max-w-[70%]', pending && 'opacity-70')}>
        <div
          className="rounded-lg border px-[14px] py-[11px] text-[13.5px] leading-[1.6] text-[var(--co-ink-2)] [overflow-wrap:anywhere]"
          style={
            isBot
              ? { borderColor: 'var(--co-machine-line)', background: 'var(--co-machine-wash)' }
              : mine
                ? { borderColor: 'var(--co-line-strong)', background: 'var(--co-brass-wash)' }
                : { borderColor: 'var(--co-line)', background: 'var(--co-selected)' }
          }
        >
          {isBot ? (
            <MarkdownRenderer
              content={sanitizeContent(message.content)}
              className="text-[13.5px] leading-[1.6]"
            />
          ) : (
            <p className="whitespace-pre-wrap">{message.content}</p>
          )}
        </div>
        <div
          className={clsx(
            'co-mono mt-[5px] text-[9.5px] uppercase tracking-[.08em]',
            mine && 'text-right',
          )}
          style={{ color: isBot ? 'var(--co-machine-ink)' : 'var(--co-ink-3)' }}
        >
          {who} · {pending ? 'sending…' : clock(message.createdAt)}
          {isBot && ' · machine'}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-3 border-b border-[var(--co-line-subtle)] py-[9px] last:border-b-0">
      <span className="co-mono w-[98px] flex-none text-[9.5px] font-semibold uppercase tracking-[.12em] text-[var(--co-ink-3)]">
        {label}
      </span>
      <span className="min-w-0 flex-1 break-words text-[12.5px] text-[var(--co-ink)]">{value}</span>
    </div>
  );
}

/** "website_widget" -> "Website widget". */
function humanize(value: string): string {
  const t = value.replace(/[_-]+/g, ' ').trim();
  return t ? t[0].toUpperCase() + t.slice(1) : t;
}

/**
 * Who this is and how the chat has gone so far. Only what the session and its
 * messages actually carry: no invented location, visit count or score.
 */
function VisitorCard({
  session,
  messages,
  agentNames,
  myAgentId,
  now,
}: {
  session: ChatSession | null;
  messages: ChatMessage[];
  agentNames: Record<string, string>;
  myAgentId?: string;
  now: number;
}) {
  const meta = session?.metadata ?? {};
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const visitor: { label: string; value: string }[] = [];
  const chat: { label: string; value: string }[] = [];

  if (session) {
    const timeline = chatTimeline(session, messages, now);
    const email = str(meta.visitorEmail) ?? str(meta.email);
    const phone = str(meta.visitorPhone) ?? str(meta.phone);
    const language = str(meta.preferredLanguage) ?? str(meta.language);
    if (str(meta.visitorName)) visitor.push({ label: 'Name', value: str(meta.visitorName)! });
    if (email) visitor.push({ label: 'Email', value: email });
    if (phone) visitor.push({ label: 'Phone', value: phone });
    if (language) visitor.push({ label: 'Language', value: language.toUpperCase() });
    if (session.customerId || meta.resolvedCustomerId) {
      visitor.push({ label: 'Customer', value: meta.customerIsNew ? 'New customer' : 'Known customer' });
    }
    if (str(meta.pageUrl)) visitor.push({ label: 'Page', value: str(meta.pageUrl)! });
    if (str(meta.browserInfo)) visitor.push({ label: 'Browser', value: str(meta.browserInfo)! });

    const holder = !session.agentId
      ? session.status === ChatSessionStatus.WAITING
        ? 'Nobody yet (in the queue)'
        : session.status === ChatSessionStatus.BOT
          ? 'The assistant'
          : 'Nobody'
      : session.agentId === myAgentId
        ? 'You'
        : agentNames[session.agentId] || 'Another agent';
    chat.push({ label: 'Handled by', value: holder });
    chat.push({ label: 'Source', value: humanize(str(meta.source) ?? session.channel ?? 'web chat') });
    chat.push({ label: 'Started', value: clock(session.startedAt) });
    if (timeline.handedOffAt) chat.push({ label: 'Handed off', value: clock(timeline.handedOffAt) });
    if (timeline.acceptedAt) chat.push({ label: 'Accepted', value: clock(timeline.acceptedAt) });
    if (timeline.waitBeforeAcceptSeconds !== null) {
      chat.push({ label: 'Queue wait', value: waitClock(timeline.waitBeforeAcceptSeconds) });
    }
    if (timeline.handleSeconds !== null) {
      chat.push({
        label: session.endedAt ? 'Handle time' : 'With agent',
        value: duration(timeline.handleSeconds),
      });
    }
    if (session.endedAt) chat.push({ label: 'Ended', value: clock(session.endedAt) });
    const visitorTurns = messages.filter(
      (m) => m.senderType === SenderType.VISITOR || m.senderType === SenderType.CUSTOMER,
    ).length;
    if (messages.length) {
      chat.push({ label: 'Messages', value: `${messages.length} (${visitorTurns} from the visitor)` });
    }
    const mood = sentimentLabel(meta.lastAiSentiment);
    if (mood) chat.push({ label: 'Sentiment', value: mood });
    if (str(meta.lastAiDominantEmotion)) {
      chat.push({ label: 'Emotion', value: humanize(str(meta.lastAiDominantEmotion)!) });
    }
    if (str(meta.lastAiIntent)) chat.push({ label: 'Intent', value: humanize(str(meta.lastAiIntent)!) });
  }

  return (
    <div className={clsx(CARD_CLASS, 'co-scrollbar-none flex-none overflow-y-auto px-[18px] py-4')} style={{ ...CARD, maxHeight: '58%' }}>
      <span className="co-mono text-[10px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
        Visitor
      </span>
      {!session ? (
        <p className="mt-[11px] text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
          Open a conversation to see who is on the other end.
        </p>
      ) : (
        <>
          <div className="mt-[11px]">
            {visitor.map((f) => (
              <Field key={f.label} label={f.label} value={f.value} />
            ))}
          </div>
          <span className="co-mono mt-4 block text-[10px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
            This chat
          </span>
          <div className="mt-[7px]">
            {chat.map((f) => (
              <Field key={f.label} label={f.label} value={f.value} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** The day at a glance: the queue, and (admin) the team; (agent) their own numbers. */
function InsightStrip({ stats, seesAll }: { stats: ChatStats | null; seesAll: boolean }) {
  const longest = stats?.longestWaitSeconds ?? null;
  const tiles: { label: string; value: string; alert?: boolean; hint?: string }[] = seesAll
    ? [
        { label: 'In queue', value: stats ? String(stats.waiting) : '—', alert: (stats?.waiting ?? 0) > 0 },
        { label: 'Longest wait', value: waitClock(longest), alert: waitTone(longest) === 'danger' },
        { label: 'With agents', value: stats ? String(stats.active) : '—' },
        { label: 'With the bot', value: stats?.bot !== undefined ? String(stats.bot) : '—' },
        { label: 'Closed today', value: stats ? String(stats.endedToday) : '—' },
        {
          label: 'Avg queue wait',
          value: waitClock(stats?.avgWaitToAcceptSeconds ?? null),
          hint: 'From joining the queue to an agent accepting, today.',
        },
        {
          label: 'Avg handle time',
          value: duration(stats?.avgHandleSeconds ?? null),
          hint: 'From accepting to closing, for chats closed today.',
        },
        {
          label: 'Agents online',
          value: stats?.agents ? `${stats.agents.filter((a) => a.online).length}` : '—',
          hint: 'Agents with a chat today who are connected now.',
        },
      ]
    : [
        { label: 'In queue', value: stats ? String(stats.waiting) : '—', alert: (stats?.waiting ?? 0) > 0 },
        { label: 'Longest wait', value: waitClock(longest), alert: waitTone(longest) === 'danger' },
        { label: 'My chats', value: stats?.mine ? String(stats.mine.active) : '—' },
        { label: 'I closed today', value: stats?.mine ? String(stats.mine.endedToday) : '—' },
        {
          label: 'My avg handle',
          value: duration(stats?.mine?.avgHandleSeconds ?? null),
          hint: 'From accepting to closing, for your chats closed today.',
        },
      ];

  return (
    <section
      aria-label="Live chat at a glance"
      // An even grid: one row from 1280px, two equal rows below it. A wrapping
      // flex row left the last tile alone on a line of its own at 1024.
      className={clsx(
        CARD_CLASS,
        'grid flex-none overflow-hidden [grid-template-columns:repeat(var(--cols-narrow),minmax(0,1fr))] xl:[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]',
      )}
      style={
        {
          ...CARD,
          '--cols': tiles.length,
          // Up to six tiles stay on one row at every width; only the admin's
          // eight split, into two equal rows. Five used to break 3 + 2.
          '--cols-narrow': tiles.length > 6 ? Math.ceil(tiles.length / 2) : tiles.length,
        } as React.CSSProperties
      }
    >
      {tiles.map((t) => (
        <div
          key={t.label}
          title={t.hint}
          // The figure sits on the bottom edge, so a label that wraps to two
          // lines does not push its figure below the others.
          className="-ml-px -mt-px flex min-w-0 flex-col justify-between border-l border-t border-[var(--co-line-subtle)] px-4 py-[11px]"
        >
          <div className="co-mono text-[9.5px] font-semibold uppercase tracking-[.14em] text-[var(--co-ink-3)]">
            {t.label}
          </div>
          <div
            className="mt-1 text-[20px] font-bold tabular-nums leading-[1.1]"
            style={{ color: t.alert ? 'var(--co-terracotta)' : 'var(--co-ink)' }}
          >
            {t.value}
          </div>
        </div>
      ))}
    </section>
  );
}

/** Admin, nothing open: who is carrying what today. A row filters the list to that agent. */
function TeamCard({
  stats,
  agents,
  onPick,
}: {
  stats: ChatStats | null;
  agents: TeamMember[];
  onPick: (agentId: string) => void;
}) {
  const byId = new Map((stats?.agents ?? []).map((a) => [a.agentId, a]));
  // Everyone on the team, busiest first; those with no chat today at the bottom.
  const rows = agents
    .map((m) => ({ member: m, figures: byId.get(m.id) }))
    .sort(
      (a, b) =>
        (b.figures?.active ?? -1) - (a.figures?.active ?? -1) ||
        (b.figures?.endedToday ?? 0) - (a.figures?.endedToday ?? 0),
    );

  return (
    <div className={clsx(CARD_CLASS, 'flex min-h-0 flex-1 flex-col overflow-hidden px-[18px] py-4')} style={CARD}>
      <span className="co-mono flex-none text-[10px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
        Team today
      </span>
      <div className="co-mono mt-3 flex flex-none gap-2 border-b border-[var(--co-line-subtle)] pb-2 text-[9px] uppercase tracking-[.12em] text-[var(--co-ink-3)]">
        <span className="flex-1">Agent</span>
        <span className="w-[34px] text-right">Live</span>
        <span className="w-[44px] text-right">Closed</span>
        <span className="w-[42px] text-right">Avg</span>
      </div>
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
        {rows.length === 0 ? (
          <p className="py-6 text-[12.5px] text-[var(--co-ink-3)]">No team members to show.</p>
        ) : (
          rows.map(({ member, figures }) => (
            <button
              key={member.id}
              type="button"
              onClick={() => onPick(member.id)}
              className="flex w-full items-center gap-2 border-b border-[var(--co-line-subtle)] py-[9px] text-left last:border-b-0 hover:bg-[var(--co-selected)]"
              title={`Show ${memberName(member)}'s chats`}
            >
              <span
                aria-label={figures?.online ? 'online' : 'offline'}
                className="h-[7px] w-[7px] flex-none rounded-full"
                style={{ background: figures?.online ? 'var(--co-green)' : 'var(--co-line-strong)' }}
              />
              <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--co-ink)]">{memberName(member)}</span>
              <span className="w-[34px] text-right text-[12.5px] font-semibold tabular-nums text-[var(--co-ink)]">
                {figures?.active ?? 0}
              </span>
              <span className="w-[44px] text-right text-[12.5px] tabular-nums text-[var(--co-ink-2)]">
                {figures?.endedToday ?? 0}
              </span>
              <span className="w-[42px] text-right text-[11.5px] tabular-nums text-[var(--co-ink-3)]">
                {duration(figures?.avgHandleSeconds ?? null)}
              </span>
            </button>
          ))
        )}
      </div>
      <p className="mt-3 flex-none border-t border-[var(--co-line-subtle)] pt-3 text-[11.5px] leading-[1.5] text-[var(--co-ink-3)]">
        Live = chats an agent holds now. Avg = handle time of today's closed chats. Pick an agent to see their chats.
      </p>
    </div>
  );
}

function ticketDue(t: Ticket, now: number): { label: string; over: boolean } | null {
  const iso = t.resolutionDueAt || t.firstResponseDueAt;
  if (!iso) return null;
  const due = new Date(iso).getTime();
  if (Number.isNaN(due)) return null;
  const over = due < now;
  return { label: `${duration(Math.abs(due - now) / 1000)} ${over ? 'over' : 'left'}`, over };
}

function OpenTicketsCard({
  session,
  tickets,
  linkedTicketNumber,
  now,
  onOpen,
}: {
  session: ChatSession | null;
  tickets: Ticket[] | null;
  linkedTicketNumber: string | null;
  now: number;
  onOpen: (id: string) => void;
}) {
  return (
    <div
      className={clsx(CARD_CLASS, 'flex min-h-0 flex-1 flex-col overflow-hidden px-[18px] py-4')}
      style={CARD}
    >
      <span className="co-mono flex-none text-[10px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
        Open tickets
      </span>

      <div className="co-scrollbar-none mt-[13px] flex min-h-0 flex-1 flex-col gap-[10px] overflow-y-auto">
        {!session ? (
          <p className="text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
            Nothing open here yet.
          </p>
        ) : tickets === null ? (
          <p className="text-[12.5px] text-[var(--co-ink-3)]">Looking…</p>
        ) : tickets.length === 0 ? (
          <p className="text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
            This visitor has nothing else open.
          </p>
        ) : (
          tickets.map((t) => {
            const due = ticketDue(t, now);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onOpen(t.id)}
                className="rounded-lg border px-3 py-[11px] text-left"
                style={
                  due?.over
                    ? {
                        borderColor: 'var(--co-burgundy-line)',
                        background: 'var(--co-burgundy-wash)',
                      }
                    : { borderColor: 'var(--co-line)', background: 'var(--co-raised)' }
                }
              >
                <div className="line-clamp-2 text-[12.5px] font-semibold leading-[1.4] text-[var(--co-ink)]">
                  {t.subject}
                </div>
                <div className="mt-[6px] flex items-center gap-2">
                  <span className="co-mono text-[10px] text-[var(--co-ink-3)]">
                    {t.ticketNumber}
                  </span>
                  <div className="flex-1" />
                  {due && (
                    <span
                      className="co-mono text-[10px] font-semibold"
                      style={{ color: due.over ? 'var(--co-burgundy)' : 'var(--co-ink-2)' }}
                    >
                      {due.label}
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      {session?.ticketId && (
        <button
          type="button"
          onClick={() => onOpen(session.ticketId as string)}
          className="mt-3 flex-none border-t border-[var(--co-line-subtle)] pt-3 text-left text-[12px] leading-[1.5] text-[var(--co-brass-ink)] hover:underline"
        >
          This chat is already on {linkedTicketNumber ? `#${linkedTicketNumber}` : 'a ticket'} →
        </button>
      )}

      {session && !session.ticketId && tickets && tickets.length > 0 && (
        <p className="mt-3 flex-none border-t border-[var(--co-line-subtle)] pt-3 text-[12px] leading-[1.5] text-[var(--co-ink-3)]">
          If this chat is the same problem as one of those, end it onto that ticket rather than
          answering twice.
        </p>
      )}
    </div>
  );
}

/** Ending a chat is where it becomes a ticket — the one decision worth a dialog. */
function EndChatModal({
  onConfirm,
  onCancel,
  isLoading,
  firstCustomerMessage,
}: {
  onConfirm: (options: {
    createTicket: boolean;
    ticketSubject: string;
    ticketStatus: 'new' | 'resolved';
  }) => void;
  onCancel: () => void;
  isLoading: boolean;
  firstCustomerMessage?: string;
}) {
  const [createTicket, setCreateTicket] = useState(true);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketStatus, setTicketStatus] = useState<'new' | 'resolved'>('new');

  return (
    <div
      className={clsx(CARD_CLASS, 'w-full max-w-[460px] p-6')}
      style={{ borderRadius: 10, boxShadow: 'var(--co-shadow-pop)' }}
      onClick={(e) => e.stopPropagation()}
    >
      <h2 className="text-[19px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
        End this chat
      </h2>
      <p className="mt-2 text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
        The transcript is kept either way. A ticket is what carries the problem forward.
      </p>

      <label className="mt-5 flex cursor-pointer items-center gap-3">
        <input
          type="checkbox"
          checked={createTicket}
          onChange={(e) => setCreateTicket(e.target.checked)}
          className="h-4 w-4 accent-[var(--co-brass-ink)]"
        />
        <span className="text-[13px] font-semibold text-[var(--co-ink)]">
          Carry it onto a ticket
        </span>
      </label>

      {createTicket ? (
        <div className="ml-7 mt-4 border-l-2 border-[var(--co-line)] pl-4">
          <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
            Subject
          </span>
          <input
            type="text"
            value={ticketSubject}
            onChange={(e) => setTicketSubject(e.target.value)}
            placeholder={firstCustomerMessage || "Taken from the visitor's first message"}
            className="mt-[7px] h-[34px] w-full rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-3)] focus:border-[var(--co-brass)] focus:outline-none"
          />

          <div className="mt-4 flex flex-col gap-[7px]">
            {(
              [
                ['new', 'Leave it open — somebody still has to act'],
                ['resolved', 'Close it — handled here, and it is mine'],
              ] as const
            ).map(([value, label]) => (
              <label
                key={value}
                className="flex cursor-pointer items-center gap-[9px] rounded-lg border px-[11px] py-[9px]"
                style={
                  ticketStatus === value
                    ? { borderColor: 'var(--co-brass)', background: 'var(--co-brass-wash)' }
                    : { borderColor: 'var(--co-line)', background: 'var(--co-raised)' }
                }
              >
                <input
                  type="radio"
                  name="ticketStatus"
                  checked={ticketStatus === value}
                  onChange={() => setTicketStatus(value)}
                  className="h-[13px] w-[13px] accent-[var(--co-brass-ink)]"
                />
                <span
                  className="text-[12.5px] text-[var(--co-ink)]"
                  style={{ fontWeight: ticketStatus === value ? 600 : 400 }}
                >
                  {label}
                </span>
              </label>
            ))}
          </div>
        </div>
      ) : (
        <p className="ml-7 mt-3 text-[12px] leading-[1.5] text-[var(--co-terracotta)]">
          Nothing will carry this forward — the chat just closes.
        </p>
      )}

      <div className="mt-6 flex justify-end gap-[9px] border-t border-[var(--co-line-subtle)] pt-4">
        <button type="button" onClick={onCancel} disabled={isLoading} className={CONTROL}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onConfirm({ createTicket, ticketSubject, ticketStatus })}
          disabled={isLoading}
          className={PRIMARY}
        >
          {isLoading ? (
            'Ending…'
          ) : (
            <>
              <X className="mr-[6px] h-[14px] w-[14px]" />
              End chat
            </>
          )}
        </button>
      </div>
    </div>
  );
}
